/**
 * Axis-aligned bounding box (AABB) atoms for 2D geometry.
 *
 * Domain-neutral: points are plain `{ x, y }` objects and no input point or
 * array is ever mutated. This module has no imports.
 *
 * Bounds comparisons here are exact — no epsilon is applied — so boxes that
 * merely touch are reported as overlapping. The absolute `1e-10` epsilon used
 * by the segment/line atoms (`./segments`) does not participate in any bounds
 * decision; callers working in normalized coordinates therefore get the same
 * pruning behavior regardless of scale.
 *
 * @module lib/geom/bounds
 */

/**
 * Bounds shape produced by this module.
 *
 * @typedef {{ minX: number, minY: number, maxX: number, maxY: number }} Bounds
 */

/**
 * Point shape accepted by this module.
 *
 * @typedef {{ x: number, y: number }} Point
 */

/**
 * Writes the four bounds fields into `out` when provided, otherwise allocates a
 * fresh object. Keeps the `out` pattern allocation-free for hot loops.
 *
 * @param {number} minX
 * @param {number} minY
 * @param {number} maxX
 * @param {number} maxY
 * @param {Bounds} [out] optional target; when given it is written and returned
 * @returns {Bounds}
 */
function writeBounds(minX, minY, maxX, maxY, out) {
    if (out) {
        out.minX = minX;
        out.minY = minY;
        out.maxX = maxX;
        out.maxY = maxY;
        return out;
    }
    return { minX, minY, maxX, maxY };
}

/**
 * AABB covering both endpoints of a segment.
 *
 * The endpoints may be given in any order; the box is derived with
 * `Math.min`/`Math.max` on each axis. A zero-length segment yields a degenerate
 * box where `minX === maxX` and `minY === maxY` — that is a valid result, not a
 * failure. Neither endpoint is mutated.
 *
 * @param {Point} p0 first endpoint
 * @param {Point} p1 second endpoint
 * @param {Bounds} [out] optional target; when given it is written and returned (no allocation)
 * @returns {Bounds} `{ minX, minY, maxX, maxY }` covering `p0` and `p1`
 */
export function segmentBounds(p0, p1, out) {
    return writeBounds(
        Math.min(p0.x, p1.x),
        Math.min(p0.y, p1.y),
        Math.max(p0.x, p1.x),
        Math.max(p0.y, p1.y),
        out
    );
}

/**
 * AABB covering every point of a polyline.
 *
 * Degenerate input: returns `null` when `points` is not an array or is empty. A
 * single-point polyline yields a degenerate box (`minX === maxX`,
 * `minY === maxY`).
 *
 * The `closed` flag is accepted for signature symmetry with the rest of the
 * module but is a documented no-op for the result: the wrap-around segment
 * `points[n-1] → points[0]` introduces no new vertices, so a closed polyline has
 * exactly the same AABB as the open one.
 *
 * Neither the array nor its points are mutated.
 *
 * @param {Array<Point>} points polyline vertices
 * @param {boolean} [closed=false] ignored for the result (documented no-op)
 * @param {Bounds} [out] optional target; when given it is written and returned (no allocation)
 * @returns {Bounds|null} `{ minX, minY, maxX, maxY }`, or `null` for non-array or empty input
 */
export function polylineBounds(points, closed, out) {
    if (!Array.isArray(points) || points.length === 0) {
        return null;
    }

    const first = points[0];
    let minX = first.x;
    let minY = first.y;
    let maxX = first.x;
    let maxY = first.y;

    for (let i = 1; i < points.length; i++) {
        const point = points[i];
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);
    }

    return writeBounds(minX, minY, maxX, maxY, out);
}

/**
 * Tests whether two AABBs overlap.
 *
 * Returns `true` when the boxes overlap or merely touch on both axes, and
 * `false` as soon as they are disjoint on either axis. Comparisons are
 * inclusive (`<=` / `>=`), so shared edges and shared corners count as overlap.
 *
 * Degenerate input: returns `false` when either argument is `null` (or
 * otherwise absent), which is what `polylineBounds` yields for an empty
 * polyline — an empty polyline overlaps nothing.
 *
 * @param {Bounds|null} a first box
 * @param {Bounds|null} b second box
 * @returns {boolean} `true` when the boxes overlap or touch on both axes
 */
export function boundsOverlap(a, b) {
    if (!a || !b) {
        return false;
    }
    return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}
